import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { punktartSvgUrl } from '../../data-model';

/**
 * Kompass-Rose zum Setzen der Pfeilrichtung drehbarer Symbole
 * (z.B. Verhagerungspfeile). `rotation` = CSS-Grad im Uhrzeigersinn, bezogen
 * auf die im SVG gezeichnete Ausrichtung (Osten = 0°). Das gewählte Symbol
 * wird in der Mitte als Live-Vorschau gedreht angezeigt.
 *
 * Das Gating (welche Punktart überhaupt drehbar ist) macht der Aufrufer per
 * istPunktartDrehbar() — diese Komponente rendert nur die Auswahl.
 */
@Component({
	standalone: true,
	selector: 'bp-rotation-picker',
	imports: [CommonModule],
	template: `
		<div class="compass-rose">
			<button *ngFor="let r of richtungen" type="button"
				class="btn btn-sm compass-btn" [ngClass]="'dir-' + r.label"
				[class.btn-primary]="(rotation || 0) === r.deg"
				[class.btn-outline-secondary]="(rotation || 0) !== r.deg"
				(click)="select(r.deg)">{{ r.label }}</button>
			<div class="compass-center">
				<img *ngIf="type" [src]="svgUrl"
					[style.transform]="'rotate(' + (rotation || 0) + 'deg)'"
					class="compass-preview" alt="Vorschau Pfeilrichtung" title="Vorschau Pfeilrichtung">
			</div>
		</div>
	`,
	styles: [`
		.compass-rose { display: grid; grid-template-columns: repeat(3, 56px); grid-template-rows: repeat(3, 44px); gap: 4px; }
		.compass-rose .dir-NW { grid-area: 1 / 1; }
		.compass-rose .dir-N  { grid-area: 1 / 2; }
		.compass-rose .dir-NO { grid-area: 1 / 3; }
		.compass-rose .dir-W  { grid-area: 2 / 1; }
		.compass-rose .dir-O  { grid-area: 2 / 3; }
		.compass-rose .dir-SW { grid-area: 3 / 1; }
		.compass-rose .dir-S  { grid-area: 3 / 2; }
		.compass-rose .dir-SO { grid-area: 3 / 3; }
		.compass-rose .compass-center { grid-area: 2 / 2; display: flex; align-items: center; justify-content: center; }
		.compass-btn { padding: 2px 4px; }
		.compass-preview { width: 36px; height: 36px; object-fit: contain; transition: transform .15s ease; }
	`],
})
export class RotationPickerComponent {
	/** Punktart (= Nummer, z.B. "83") — für die Vorschau. */
	@Input() type = '';

	/** Aufgelöste Symbol-URL (Liste/Upload als Single Source). */
	get svgUrl(): string {
		return punktartSvgUrl(this.type);
	}
	/** Aktuelle Drehung in CSS-Grad (Osten = 0°). undefined wird wie 0 behandelt. */
	@Input() rotation?: number;
	@Output() rotationChange = new EventEmitter<number>();

	// deg = CSS-Rotation im Uhrzeigersinn, bezogen auf die nach Osten gezeichneten
	// Pfeile (O = 0°). Platzierung im 3×3-Raster per CSS-Klasse `dir-<label>`.
	readonly richtungen: { label: string; deg: number }[] = [
		{ label: 'NW', deg: 225 },
		{ label: 'N',  deg: 270 },
		{ label: 'NO', deg: 315 },
		{ label: 'W',  deg: 180 },
		{ label: 'O',  deg: 0   },
		{ label: 'SW', deg: 135 },
		{ label: 'S',  deg: 90  },
		{ label: 'SO', deg: 45  },
	];

	select(deg: number): void {
		this.rotation = deg;
		this.rotationChange.emit(deg);
	}
}
